// 面板緊急求救提醒（docs/panel 07-dashboard §3.5、D11）。狀態與輪詢見 panelSos.ts；掛在 AppShell（全 App 範圍）。
//   - PanelSosOverlay：有人按了求救、還沒人回覆、我也沒按過 → 全螢幕紅色，蓋在所有畫面上（含門鈴卡）。
//     ［我知道了］= ack；［看面板］= 先收成橫幅並到面板頁。沒有 Esc / 點外面關閉：一定要選一個。
//   - PanelSosBanners：頁面上方小橫幅（我已回覆 / 別人已回覆 +［我也知道了］/ 先收起的 +［我知道了］），直到雲端不再列出。
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { classifySos, sosAgo, type PanelSos } from './panels';
import { panelSos } from './panelSos';
import './panelSos.css';

/** AppShell 掛載（= 已登入）時開始輪詢，卸載時停 */
export function usePanelSosPolling(): void {
  useEffect(() => panelSos.start(), []);
}

function usePanelSos() {
  const st = useSyncExternalStore(panelSos.subscribe, panelSos.get);
  return { st, ...classifySos(st.list, st.mine, st.minimized, st.dismissed, Math.floor((st.fetchedAt || Date.now()) / 1000)) };
}

/** 「〇〇面板」：名字裡任何地方已經有「面板 / パネル / panel」就不再加（「玄關面板（iPad）」不能變「玄關面板（iPad）面板」，2026-10-16） */
export function panelLabel(t: TFunction, s: PanelSos): string {
  // 主人設了「打電話時顯示的名字」（例如「媽媽」）→ 直接用，不加「面板」（John 2026-10-22：「媽媽 緊急求救」）
  if (s.caller_name?.trim()) return s.caller_name.trim();
  const name = s.panel_name.trim();
  if (!name) return t('panelSos.unnamedPanel');
  const suffix = t('panelSos.panelSuffix');
  const lower = name.toLowerCase();
  const has = ['面板', 'パネル', 'panel', suffix.trim().toLowerCase()].some((w) => w && lower.includes(w));
  return has ? name : name + suffix;
}
/** 用了 caller_name 且跟面板名稱不同 → 小字補面板名稱（知道是哪一台） */
export function panelSubName(s: PanelSos): string | null {
  const c = s.caller_name?.trim();
  const n = s.panel_name.trim();
  return c && n && c !== n ? n : null;
}
const names = (t: TFunction, s: PanelSos) => s.acked_by.join(t('panelSos.nameSep'));

function when(t: TFunction, lang: string, s: PanelSos, nowMs: number): string {
  const [key, n] = sosAgo(s.at, nowMs);
  const time = new Date(s.at * 1000).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  return t('panelSos.when', { time, ago: t(`panelSos.${key}`, { count: n }) });
}

/** 大標題：面板名稱與其餘文字各自成一塊，只在兩塊之間換行（不會斷成「緊急／求救」） */
function SosTitle({ t, panel }: { t: TFunction; panel: string }) {
  const MARK = '\u0000';
  const parts = t('panelSos.title', { panel: MARK }).split(MARK);
  return (
    <>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className="sos-title-chunk sos-title-panel">{panel}</span>}
          {p && <span className="sos-title-chunk">{p}</span>}
        </span>
      ))}
    </>
  );
}

export function PanelSosOverlay() {
  const { t, i18n } = useTranslation();
  const { st, alert } = usePanelSos();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  useFocusTrap(!!alert, cardRef);
  if (!alert) return null;
  const busy = st.acking === alert.sos_id;
  const view = () => {
    panelSos.minimize(alert.sos_id);
    navigate(`/devices/${encodeURIComponent(alert.panel_device_id)}`);
  };
  return (
    <div className="sos-scrim" role="alertdialog" aria-modal="true" aria-labelledby="sos-title" aria-describedby="sos-note" data-testid="panel-sos-alert" data-sos-id={alert.sos_id}>
      <div className="sos-card" ref={cardRef}>
        <div className="sos-icon" aria-hidden="true">
          !
        </div>
        <h1 id="sos-title" className="sos-title">
          <SosTitle t={t} panel={panelLabel(t, alert)} />
        </h1>
        {panelSubName(alert) && (
          <div className="sos-when" data-testid="panel-sos-panel-name">
            {panelSubName(alert)}
          </div>
        )}
        <div className="sos-when" data-testid="panel-sos-when">
          {when(t, i18n.language, alert, st.fetchedAt || Date.now())}
        </div>
        <p id="sos-note" className="sos-note">
          {t('panelSos.not119')}
        </p>
        {st.ackFailed === alert.sos_id && (
          <p className="sos-err" role="status">
            {t('panelSos.ackFailed')}
          </p>
        )}
        <div className="sos-actions">
          <button className="sos-btn sos-btn--ack" onClick={() => void panelSos.ack(alert.sos_id)} disabled={busy} data-autofocus>
            {busy ? t('panelSos.acking') : t('panelSos.ack')}
          </button>
          <button className="sos-btn sos-btn--view" onClick={view}>
            {t('panelSos.viewPanel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PanelSosBanners() {
  const { t, i18n } = useTranslation();
  const { st, banners } = usePanelSos();
  if (!banners.length) return null;
  return (
    <div className="sos-banners" data-testid="panel-sos-banners">
      {banners.map(({ sos, show }) => {
        const panel = panelLabel(t, sos);
        const busy = st.acking === sos.sos_id;
        const text =
          show === 'mine'
            ? sos.acked_by.length
              ? t('panelSos.bannerMine', { panel, names: names(t, sos) })
              : t('panelSos.bannerMineNoName', { panel })
            : show === 'others'
              ? t('panelSos.bannerOthers', { panel, names: names(t, sos) })
              : t('panelSos.title', { panel });
        return (
          <div key={sos.sos_id} className={`sos-banner sos-banner--${show}`} role="status" data-testid={`panel-sos-banner-${show}`} data-sos-id={sos.sos_id}>
            <span className="sos-banner-dot" aria-hidden="true" />
            <span className="sos-banner-text">
              {text}
              {panelSubName(sos) && <span className="sos-banner-when">{panelSubName(sos)}</span>}
              <span className="sos-banner-when">{when(t, i18n.language, sos, st.fetchedAt || Date.now())}</span>
              {st.ackFailed === sos.sos_id && <span className="sos-banner-err">{t('panelSos.ackFailed')}</span>}
            </span>
            {show === 'mine' && (
              <button className="sos-banner-btn sos-banner-btn--ghost" onClick={() => panelSos.dismiss(sos.sos_id)} data-testid="panel-sos-dismiss">
                {t('panelSos.hide')}
              </button>
            )}
            {show !== 'mine' && (
              <button className="sos-banner-btn" onClick={() => void panelSos.ack(sos.sos_id)} disabled={busy}>
                {busy ? t('panelSos.acking') : show === 'others' ? t('panelSos.ackToo') : t('panelSos.ack')}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
